import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'

export interface ChatNotification {
  id: number
  conversationId: number
  conversationName: string
  senderName: string
  message: string
  timestamp: Date
  read: boolean
}

export const useChatNotificationsStore = defineStore('chatNotifications', () => {
  const notifications = ref<ChatNotification[]>([])
  const totalUnreadCount = ref(0)
  const soundEnabled = ref(true)
  const notificationVolume = ref(50)
  const notificationSound = ref('notification.mp3')
  let audioPlayer: HTMLAudioElement | null = null
  let pollingInterval: number | null = null

  // Computed
  const unreadCount = computed(() => notifications.value.filter((n) => !n.read).length)

  // Actions
  const fetchUnreadMessages = async () => {
    try {
      const userStr = localStorage.getItem('user')
      if (!userStr) return

      const user = JSON.parse(userStr)
      const currentUserId = user.id

      const response = await axios.get(`${apiBaseUrl}/conversations`, { withCredentials: true })

      if (response.data.success) {
        const conversations = response.data.data
        const newNotifications: ChatNotification[] = []

        for (const conv of conversations) {
          if (conv.unreadCount > 0 && conv.lastMessage) {
            console.log(
              `[Notifications] Conversation: ${conv.name}, Unread: ${conv.unreadCount}, LastMessage: ${conv.lastMessage}`,
            )

            // Vérifier si cette notification existe déjà
            const existingNotif = notifications.value.find(
              (n) => n.conversationId === conv.id && n.message === conv.lastMessage,
            )

            if (!existingNotif) {
              newNotifications.push({
                id: Date.now() + conv.id,
                conversationId: conv.id,
                conversationName: conv.name,
                senderName: conv.participants[0]?.fullName || 'Inconnu',
                message: conv.lastMessage,
                timestamp: new Date(conv.lastMessageAt),
                read: false,
              })
              console.log(`[Notifications] ✅ Nouvelle notification créée pour: ${conv.name}`)
            }
          }
        }

        // Jouer le son seulement s'il y a de nouvelles notifications
        if (newNotifications.length > 0) {
          notifications.value.push(...newNotifications)
          playNotificationSound()
          console.log(`[Notifications] 🔔 ${newNotifications.length} nouvelle(s) notification(s)`)
        }

        // Mettre à jour le compteur total
        totalUnreadCount.value = conversations.reduce((sum: number, conv: any) => sum + (conv.unreadCount || 0), 0)
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des messages non lus:', error)
    }
  }

  const playNotificationSound = () => {
    if (!soundEnabled.value) return

    try {
      // Construire l'URL sans /api car /uploads est servi directement par Spring Boot
      const baseUrl = apiBaseUrl.replace('/api', '')
      const soundPath = `${baseUrl}/uploads/sound_effects/${notificationSound.value}`

      if (audioPlayer) {
        audioPlayer.pause()
        audioPlayer.currentTime = 0
      }

      audioPlayer = new Audio(soundPath)
      audioPlayer.crossOrigin = 'use-credentials'
      audioPlayer.volume = notificationVolume.value / 100

      // Gérer les erreurs d'autoplay et de chargement silencieusement
      audioPlayer.play().catch((error) => {
        // Ne logger que si ce n'est pas une erreur d'autoplay ou de permission
        if (
          !error.message.includes('play() can only be initiated by a user gesture') &&
          !error.message.includes('request was denied') &&
          !error.message.includes('not allowed by the user agent')
        ) {
          console.warn('Son de notification non disponible:', notificationSound.value)
        }
      })

      // Gérer les erreurs de chargement (404)
      audioPlayer.addEventListener(
        'error',
        () => {
          console.warn('Fichier audio introuvable:', notificationSound.value)
        },
        { once: true },
      )
    } catch (error) {
      console.error('Erreur lors de la lecture du son de notification:', error)
    }
  }

  const markAsRead = async (notificationId: number) => {
    const notif = notifications.value.find((n) => n.id === notificationId)
    if (notif) {
      try {
        // Appeler l'API pour marquer les messages comme lus
        await axios.post(
          `${apiBaseUrl}/conversations/${notif.conversationId}/messages/read`,
          {},
          { withCredentials: true },
        )
        console.log(`[Notifications] ✅ Messages marqués comme lus pour conversation ${notif.conversationId}`)

        // Supprimer la notification localement
        notifications.value = notifications.value.filter((n) => n.id !== notificationId)

        // Rafraîchir pour mettre à jour le compteur
        fetchUnreadMessages()
      } catch (error) {
        console.error('Erreur lors du marquage comme lu:', error)
      }
    }
  }

  const markAllAsRead = async () => {
    try {
      // Obtenir toutes les conversations uniques des notifications
      const conversationIds = [...new Set(notifications.value.map((n) => n.conversationId))]

      // Marquer comme lu pour chaque conversation
      const promises = conversationIds.map((conversationId) =>
        axios.post(`${apiBaseUrl}/conversations/${conversationId}/messages/read`, {}, { withCredentials: true }),
      )

      await Promise.all(promises)
      console.log(
        `[Notifications] ✅ Tous les messages marqués comme lus pour ${conversationIds.length} conversation(s)`,
      )

      // Vider toutes les notifications localement
      notifications.value = []

      // Rafraîchir pour mettre à jour le compteur
      await fetchUnreadMessages()
    } catch (error) {
      console.error('Erreur lors du marquage global comme lu:', error)
    }
  }

  const clearNotification = async (notificationId: number) => {
    const notif = notifications.value.find((n) => n.id === notificationId)
    if (notif) {
      try {
        // Appeler l'API pour marquer les messages comme lus
        await axios.post(
          `${apiBaseUrl}/conversations/${notif.conversationId}/messages/read`,
          {},
          { withCredentials: true },
        )
        console.log(
          `[Notifications] ✅ Notification supprimée et messages marqués comme lus pour conversation ${notif.conversationId}`,
        )

        // Supprimer la notification localement
        notifications.value = notifications.value.filter((n) => n.id !== notificationId)

        // Rafraîchir pour mettre à jour le compteur
        await fetchUnreadMessages()
      } catch (error) {
        console.error('Erreur lors de la suppression de la notification:', error)
      }
    }
  }

  const clearAllNotifications = async () => {
    try {
      // Obtenir toutes les conversations uniques des notifications
      const conversationIds = [...new Set(notifications.value.map((n) => n.conversationId))]

      // Marquer comme lu pour chaque conversation
      const promises = conversationIds.map((conversationId) =>
        axios.post(`${apiBaseUrl}/conversations/${conversationId}/messages/read`, {}, { withCredentials: true }),
      )

      await Promise.all(promises)
      console.log(`[Notifications] ✅ Toutes les notifications supprimées et messages marqués comme lus`)

      // Vider toutes les notifications
      notifications.value = []

      // Rafraîchir
      await fetchUnreadMessages()
    } catch (error) {
      console.error('Erreur lors de la suppression de toutes les notifications:', error)
    }
  }

  const startPolling = () => {
    // Ne pas démarrer si déjà actif
    if (pollingInterval) {
      console.log('🔔 Polling des notifications déjà actif')
      return
    }

    // Charger immédiatement
    fetchUnreadMessages()

    // Puis toutes les 5 secondes
    pollingInterval = window.setInterval(() => {
      fetchUnreadMessages()
    }, 5000)

    console.log('🔔 Polling des notifications de chat démarré')
  }

  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval)
      pollingInterval = null
      console.log('🔔 Polling des notifications de chat arrêté')
    }
  }

  const loadChatSettings = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/chatbox-settings`, { withCredentials: true })
      if (response.data.success) {
        soundEnabled.value = response.data.data.soundNotifications
        notificationVolume.value = response.data.data.notificationVolume
        notificationSound.value = response.data.data.notificationSound
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres chat:', error)
    }
  }

  // Ajouter une notification d'email
  const addEmailNotification = (email: { id: number; from: string; subject: string; timestamp: Date }) => {
    // Vérifier si cette notification existe déjà
    const existingNotif = notifications.value.find((n) => n.conversationId === email.id && n.message === email.subject)

    if (!existingNotif) {
      notifications.value.push({
        id: Date.now() + email.id + 10000, // Décalage pour éviter collision avec chat
        conversationId: email.id,
        conversationName: '📧 Nouvel email',
        senderName: email.from,
        message: email.subject,
        timestamp: email.timestamp,
        read: false,
      })
      playNotificationSound()
      console.log(`[Notifications] 📧 Notification email: ${email.subject}`)
    }
  }

  // Supprimer les notifications d'un email spécifique
  const removeEmailNotification = (emailId: number) => {
    notifications.value = notifications.value.filter(
      (n) => !(n.conversationName === '📧 Nouvel email' && n.conversationId === emailId),
    )
    console.log(`[Notifications] ❌ Notification email supprimée: ${emailId}`)
  }

  // Ajouter une notification de transfert
  const addTransferNotification = (transfer: { id: number; from: string; amount: number; timestamp: Date }) => {
    // Vérifier si cette notification existe déjà
    const existingNotif = notifications.value.find(
      (n) => n.conversationId === transfer.id && n.conversationName === '💸 Transfert reçu',
    )

    if (!existingNotif) {
      notifications.value.push({
        id: Date.now() + transfer.id + 20000, // Décalage pour éviter collision
        conversationId: transfer.id,
        conversationName: '💸 Transfert reçu',
        senderName: transfer.from,
        message: `${new Intl.NumberFormat('fr-FR').format(transfer.amount)} FCFA`,
        timestamp: transfer.timestamp,
        read: false,
      })
      playNotificationSound()
      console.log(`[Notifications] 💸 Notification transfert: ${transfer.amount} FCFA de ${transfer.from}`)
    }
  }

  // Supprimer les notifications d'un transfert spécifique
  const removeTransferNotification = (transferId: number) => {
    notifications.value = notifications.value.filter(
      (n) => !(n.conversationName === '💸 Transfert reçu' && n.conversationId === transferId),
    )
    console.log(`[Notifications] ❌ Notification transfert supprimée: ${transferId}`)
  }

  // Synchronisation globale des emails
  let emailSyncInterval: any = null

  const startEmailSync = (userId: number, apiBaseUrl: string) => {
    // Arrêter toute synchronisation existante
    if (emailSyncInterval) {
      clearInterval(emailSyncInterval)
    }

    // Fonction de synchronisation
    const syncEmails = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/emails/sync`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': userId.toString(),
          },
        })

        const data = await response.json()

        if (data && data.success && data.newEmailsCount > 0) {
          // Récupérer les nouveaux emails
          const emailsResponse = await fetch(`${apiBaseUrl}/emails/folder/1`, {
            credentials: 'include',
            headers: { 'X-User-Id': userId.toString() },
          })

          const emailsData = await emailsResponse.json()

          if (emailsData && emailsData.data) {
            const latestEmails = emailsData.data.slice(0, data.newEmailsCount)

            latestEmails.forEach((email: any) => {
              addEmailNotification({
                id: email.id,
                from: email.fromName || email.fromAddress || 'Expéditeur inconnu',
                subject: email.subject || '(Sans objet)',
                timestamp: new Date(email.receivedAt),
              })
            })
          }
        }
      } catch (error) {
        console.error('[Notifications] ❌ Erreur sync emails:', error)
      }
    }

    // Synchroniser immédiatement
    syncEmails()

    // Puis toutes les 20 secondes
    emailSyncInterval = setInterval(syncEmails, 20000)
    console.log('[Notifications] ✅ Synchronisation email démarrée')
  }

  const stopEmailSync = () => {
    if (emailSyncInterval) {
      clearInterval(emailSyncInterval)
      emailSyncInterval = null
      console.log('[Notifications] 🛑 Synchronisation email arrêtée')
    }
  }

  // Synchronisation globale des transferts
  let transferSyncInterval: any = null
  let lastCheckedTransfers: number[] = []

  const startTransferSync = (userId: number, apiBaseUrl: string) => {
    // Arrêter toute synchronisation existante
    if (transferSyncInterval) {
      clearInterval(transferSyncInterval)
    }

    // Fonction de synchronisation
    const syncTransfers = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/transfers/pending?userId=${userId}`, {
          credentials: 'include',
        })

        const data = await response.json()

        if (data && data.success && data.data && data.data.length > 0) {
          // Filtrer les transferts que l'utilisateur reçoit (toUserId = userId)
          const receivedTransfers = data.data.filter((t: any) => t.toUserId === userId)

          // Créer des notifications pour les nouveaux transferts
          receivedTransfers.forEach((transfer: any) => {
            // Vérifier si ce transfert a déjà été notifié
            if (!lastCheckedTransfers.includes(transfer.id)) {
              addTransferNotification({
                id: transfer.id,
                from: transfer.fromUserName || `Utilisateur ${transfer.fromUserId}`,
                amount: transfer.amount,
                timestamp: new Date(transfer.createdAt),
              })
              lastCheckedTransfers.push(transfer.id)
            }
          })

          // Nettoyer les anciens transferts de la liste (garder seulement les 50 derniers)
          if (lastCheckedTransfers.length > 50) {
            lastCheckedTransfers = lastCheckedTransfers.slice(-50)
          }
        }
      } catch (error) {
        console.error('[Notifications] ❌ Erreur sync transferts:', error)
      }
    }

    // Synchroniser immédiatement
    syncTransfers()

    // Puis toutes les 15 secondes
    transferSyncInterval = setInterval(syncTransfers, 15000)
    console.log('[Notifications] ✅ Synchronisation transferts démarrée')
  }

  const stopTransferSync = () => {
    if (transferSyncInterval) {
      clearInterval(transferSyncInterval)
      transferSyncInterval = null
      console.log('[Notifications] 🛑 Synchronisation transferts arrêtée')
    }
  }

  return {
    notifications,
    totalUnreadCount,
    unreadCount,
    fetchUnreadMessages,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAllNotifications,
    startPolling,
    stopPolling,
    loadChatSettings,
    addEmailNotification,
    removeEmailNotification,
    startEmailSync,
    stopEmailSync,
    addTransferNotification,
    removeTransferNotification,
    startTransferSync,
    stopTransferSync,
  }
})
